import React, { useMemo } from 'react';

/**
 * "App popup" tab on the event editor.
 *
 * Evan 2026-11-02: *"we need to build out the ability to do the in app popup for the event and to be
 * able to manage that from within the events manager in the admin dash."*
 *
 * Edits `shado_events.popup` (20261102000000). The phone reads it through
 * `get_event_promo_popup()`, which resolves every blank field off the event row and appends the
 * caller's own state — so most of this form can stay empty and still produce a correct card.
 *
 * ⚠️ **THE RATING REQUIREMENT IS NOT ON THIS TAB, AND THAT IS DELIBERATE.** It is the free ticket
 * item's `entitlement.min_logs`, edited on **Tickets & merch**, enforced by `event_rsvp` at the claim
 * form and completed by `trg_claim_pending_event_spots` when somebody qualifies. Putting a second
 * number here would let the popup promise entry the door refuses. The read-only line below shows
 * what the popup will quote.
 *
 * ⚠️ **ONE POPUP SHOWS AT A TIME, APP-WIDE** — the highest `priority` among published events whose
 * popup is active and whose window is open. Two active popups is not an error; the lower one simply
 * waits.
 */

const inputCls =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13px] text-white placeholder:text-mutedDim focus:border-accent focus:outline-none';
const labelCls = 'mb-1 block text-[10.5px] uppercase tracking-[0.08em] text-mutedDim';

export type EventPopupConfig = {
  active?: boolean;
  title?: string | null;
  subtitle?: string | null;
  cta?: string | null;
  on_list_title?: string | null;
  on_list_subtitle?: string | null;
  on_list_cta?: string | null;
  pending_title?: string | null;
  pending_cta?: string | null;
  locked_title?: string | null;
  locked_cta?: string | null;
  center?: { lat: number; lng: number } | null;
  radius_km?: number | null;
  ends_at?: string | null;
  show_once?: boolean;
  priority?: number | null;
  url?: string | null;
};

/** Ann Arbor, so the common case is one click rather than two coordinates typed by hand. */
const ANN_ARBOR = { lat: 42.2808, lng: -83.743 };

function Toggle({
  on,
  label,
  onChange,
}: {
  on: boolean;
  label: string;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className={`rounded-full border px-3 py-1 text-[11.5px] font-medium ${
        on ? 'border-accent bg-accent/15 text-accent' : 'border-border text-mutedDim hover:text-white'
      }`}
    >
      {label}
    </button>
  );
}

export function EventPopupTab({
  value,
  onChange,
  eventSlug,
  dateLine,
  venueName,
  eventTitle,
  minLogs,
  published,
}: {
  value: EventPopupConfig | null;
  onChange: (next: EventPopupConfig) => void;
  eventSlug: string;
  dateLine: string | null;
  venueName: string | null;
  eventTitle: string;
  /** The gate the popup will quote, read off the free ticket item. 0 = no gate on this event. */
  minLogs: number;
  published: boolean;
}) {
  const cfg = useMemo(() => value ?? {}, [value]);
  const set = (patch: Partial<EventPopupConfig>) => onChange({ ...cfg, ...patch });

  /**
   * ⚠️ MIRRORS THE RPC'S FALLBACK CHAIN EXACTLY (`coalesce(popup.subtitle, date_line, venue_name)`).
   * The point of the preview is to show what the phone will draw — if this chain and the SQL ever
   * disagree, the preview is worse than no preview.
   */
  const resolved = useMemo(
    () => ({
      title: cfg.title?.trim() || eventTitle,
      subtitle: cfg.subtitle?.trim() || dateLine?.trim() || venueName || '',
      cta: cfg.cta?.trim() || 'Claim your free ticket',
      onListTitle: cfg.on_list_title?.trim() || "You're on the list",
      pendingTitle: cfg.pending_title?.trim() || 'Your spot is being held',
      lockedTitle: cfg.locked_title?.trim() || 'Almost in',
    }),
    [cfg, eventTitle, dateLine, venueName],
  );

  const geoOn = !!cfg.center && (cfg.radius_km ?? 0) > 0;

  return (
    <div className="space-y-5">
      <div className="admin-card space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="admin-eyebrow">In-app popup</div>
            <div className="mt-1 text-[12px] text-mutedDim">
              Shown over whichever tab the user is on, six seconds after the app opens. One sighting
              per person{cfg.show_once === false ? ', then again on the next app launch' : ' per install'}.
            </div>
          </div>
          <Toggle
            on={cfg.active === true}
            label={cfg.active === true ? 'Live' : 'Off'}
            onChange={(v) => set({ active: v })}
          />
        </div>

        {cfg.active === true && !published ? (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-200">
            This event is <strong>not published</strong>, so the popup will not show. Publish it on the
            General tab.
          </div>
        ) : null}

        {/* ── The three state headlines ──────────────────────────────────────────────────────── */}
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className={labelCls}>Headline — not claimed yet</label>
            <input
              className={inputCls}
              value={cfg.title ?? ''}
              placeholder={eventTitle}
              onChange={(e) => set({ title: e.target.value || null })}
            />
          </div>
          <div>
            <label className={labelCls}>Button — not claimed yet</label>
            <input
              className={inputCls}
              value={cfg.cta ?? ''}
              placeholder="Claim your free ticket"
              onChange={(e) => set({ cta: e.target.value || null })}
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Sub-line (blank → the event’s date line)</label>
            <input
              className={inputCls}
              value={cfg.subtitle ?? ''}
              placeholder={dateLine || venueName || 'Thursday at Verve'}
              onChange={(e) => set({ subtitle: e.target.value || null })}
            />
            {/*
              ⚠️ THIS WARNING EXISTS BECAUSE THE DATE WAS ALREADY WRONG IN TWO PLACES ONCE. The old
              global popup blob hardcoded one date while the event's own date_line said another, with
              25 people already RSVPed. Leave this blank and it cannot happen again.
            */}
            {cfg.subtitle?.trim() ? (
              <div className="mt-1 text-[11px] text-amber-300/80">
                You’ve typed a date here by hand. If the event moves, this line will not follow —
                clear it and the popup tracks the event’s own date line.
              </div>
            ) : null}
          </div>

          <div>
            <label className={labelCls}>Headline — already on the list</label>
            <input
              className={inputCls}
              value={cfg.on_list_title ?? ''}
              placeholder="You're on the list"
              onChange={(e) => set({ on_list_title: e.target.value || null })}
            />
          </div>
          <div>
            <label className={labelCls}>Button — already on the list</label>
            <input
              className={inputCls}
              value={cfg.on_list_cta ?? ''}
              placeholder="View event"
              onChange={(e) => set({ on_list_cta: e.target.value || null })}
            />
          </div>

          <div>
            <label className={labelCls}>Headline — spot held, short of the gate</label>
            <input
              className={inputCls}
              value={cfg.pending_title ?? ''}
              placeholder="Your spot is being held"
              onChange={(e) => set({ pending_title: e.target.value || null })}
            />
          </div>
          <div>
            <label className={labelCls}>Headline — not claimed, short of the gate</label>
            <input
              className={inputCls}
              value={cfg.locked_title ?? ''}
              placeholder="Almost in"
              onChange={(e) => set({ locked_title: e.target.value || null })}
            />
          </div>
        </div>
      </div>

      {/* ── The gate, read-only ─────────────────────────────────────────────────────────────── */}
      <div className="admin-card space-y-2">
        <div className="admin-eyebrow">Rating requirement</div>
        {minLogs > 0 ? (
          <div className="text-[12.5px] text-white">
            The popup quotes <strong>{minLogs} logged nights</strong>, read from the free ticket’s
            requirement.
            <div className="mt-1 text-[11.5px] text-mutedDim">
              Change it on <strong>Tickets &amp; merch</strong> → the free ticket → requirement. It is
              edited in one place on purpose: the same number is enforced at the claim form, so the
              popup can never promise entry the door refuses.
            </div>
          </div>
        ) : (
          <div className="text-[12.5px] text-mutedDim">
            No rating requirement on this event’s free ticket, so the popup shows the plain claim
            button to everyone in range and never draws a progress bar.
          </div>
        )}
      </div>

      {/* ── Targeting ───────────────────────────────────────────────────────────────────────── */}
      <div className="admin-card space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="admin-eyebrow">Who sees it</div>
            <div className="mt-1 text-[12px] text-mutedDim">
              {geoOn
                ? `Within ${cfg.radius_km} km of the pin.`
                : 'Everyone, everywhere — no location filter.'}
            </div>
          </div>
          <Toggle
            on={geoOn}
            label={geoOn ? 'Near a pin' : 'Nationwide'}
            onChange={(v) =>
              set(v ? { center: ANN_ARBOR, radius_km: 20 } : { center: null, radius_km: null })
            }
          />
        </div>

        {/*
          ⚠️ SAY WHAT HAPPENS WITH NO LOCATION, because it is not guessable and it changes who sees
          this. The app never raises a permission dialog for a promo — it reads the cached fix only.
        */}
        {geoOn ? (
          <>
            <div className="grid gap-3 md:grid-cols-3">
              <div>
                <label className={labelCls}>Latitude</label>
                <input
                  className={inputCls}
                  value={cfg.center?.lat ?? ''}
                  onChange={(e) =>
                    set({
                      center: { lat: Number(e.target.value) || 0, lng: cfg.center?.lng ?? 0 },
                    })
                  }
                />
              </div>
              <div>
                <label className={labelCls}>Longitude</label>
                <input
                  className={inputCls}
                  value={cfg.center?.lng ?? ''}
                  onChange={(e) =>
                    set({
                      center: { lat: cfg.center?.lat ?? 0, lng: Number(e.target.value) || 0 },
                    })
                  }
                />
              </div>
              <div>
                <label className={labelCls}>Radius (km)</label>
                <input
                  className={inputCls}
                  value={cfg.radius_km ?? ''}
                  onChange={(e) => set({ radius_km: Number(e.target.value) || null })}
                />
              </div>
            </div>
            <button
              type="button"
              className="rounded-full border border-border px-3 py-1 text-[11.5px] text-mutedDim hover:text-white"
              onClick={() => set({ center: ANN_ARBOR, radius_km: 20 })}
            >
              Reset to Ann Arbor · 20 km
            </button>
            <div className="text-[11.5px] text-mutedDim">
              Someone with location off can’t be placed, so they don’t get the claim popup — but
              anyone already on the list sees their confirmation wherever they are.
            </div>
          </>
        ) : null}

        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <label className={labelCls}>Stop showing at (blank → 12h after doors)</label>
            <input
              className={inputCls}
              type="datetime-local"
              value={cfg.ends_at ? cfg.ends_at.slice(0, 16) : ''}
              onChange={(e) =>
                set({ ends_at: e.target.value ? new Date(e.target.value).toISOString() : null })
              }
            />
          </div>
          <div>
            <label className={labelCls}>Priority (higher wins)</label>
            <input
              className={inputCls}
              value={cfg.priority ?? ''}
              placeholder="0"
              onChange={(e) => set({ priority: Number(e.target.value) || 0 })}
            />
          </div>
          <div>
            <label className={labelCls}>Link (blank → this event’s page)</label>
            <input
              className={inputCls}
              value={cfg.url ?? ''}
              placeholder={`https://events.joinshado.com/${eventSlug || 'slug'}`}
              onChange={(e) => set({ url: e.target.value || null })}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Toggle
            on={cfg.show_once !== false}
            label={cfg.show_once !== false ? 'Once per install' : 'Again each app launch'}
            onChange={(v) => set({ show_once: v })}
          />
          <span className="text-[11.5px] text-mutedDim">
            Dismissing is remembered per state, so getting on the list always earns one more showing.
          </span>
        </div>
      </div>

      {/* ── Preview ─────────────────────────────────────────────────────────────────────────── */}
      <div className="admin-card">
        <div className="admin-eyebrow mb-3">Preview</div>
        <div className="grid gap-3 md:grid-cols-2">
          {(
            [
              ['Not claimed', resolved.title, resolved.cta, null],
              ['On the list', resolved.onListTitle, cfg.on_list_cta?.trim() || 'View event', null],
              minLogs > 0
                ? ['Spot held', resolved.pendingTitle, cfg.pending_cta?.trim() || 'Rate a venue', `2 of ${minLogs} nights logged`]
                : null,
              minLogs > 0
                ? ['Short of the gate', resolved.lockedTitle, cfg.locked_cta?.trim() || 'Rate a venue', `0 of ${minLogs} nights logged`]
                : null,
            ].filter(Boolean) as [string, string, string, string | null][]
          ).map(([state, title, cta, meter]) => (
            <div key={state} className="rounded-2xl border border-border bg-black/50 p-4">
              <div className="text-[9.5px] uppercase tracking-[0.14em] text-accent">{state}</div>
              <div className="mt-2 text-[17px] font-semibold leading-tight text-white">{title}</div>
              {resolved.subtitle ? (
                <div className="mt-1 text-[12px] text-mutedDim">{resolved.subtitle}</div>
              ) : null}
              {meter ? <div className="mt-3 text-[11.5px] text-white">{meter}</div> : null}
              <div className="mt-3 rounded-lg bg-accent px-3 py-2 text-center text-[12.5px] font-semibold text-white">
                {cta}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-[11px] text-mutedDim">
          Numbers in the gated cards are examples — the real ones are the viewer’s own.
        </div>
      </div>
    </div>
  );
}

export default EventPopupTab;
